/**
 * CoachDIA Discovery — Page des coachs recommandés basés sur le profil.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight, Star, Home, ArrowLeft } from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ENERGY_HEX = { red: '#C0392B', yellow: '#D4A017', green: '#2D8659', blue: '#1E4F8C' };
const ENERGY_LABEL = { red: 'Action', yellow: 'Inspiration', green: 'Harmonie', blue: 'Analyse' };

export default function DiscoveryCoachRecommendations() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/discovery/coach-recommendations`, { headers: authHeaders() });
        setData(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F1]">
      <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF8F1] px-6 text-center">
      <p className="text-[#4B5563]">Finalisez d'abord votre profil Discovery.</p>
      <Button onClick={() => navigate('/discovery')} className="mt-4 bg-[#0F0F10] text-white rounded-full">Retour</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF8F1] pb-16" data-testid="discovery-coaches-page">
      {/* ═══ Barre de navigation premium (sortie claire) ═══ */}
      <div className="sticky top-0 z-40 bg-[#FBF8F1]/95 backdrop-blur-sm border-b border-[#E5DFD0]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/my-discovery')}
            data-testid="discovery-back-to-report"
            className="flex items-center gap-2 text-[13px] text-[#4B5563] hover:text-[#0F0F10] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au rapport
          </button>
          <button
            onClick={() => navigate('/training')}
            data-testid="discovery-home-dashboard"
            className="flex items-center gap-2 text-[13px] text-[#92710A] hover:text-[#0F0F10] transition"
          >
            <Home className="w-4 h-4" />
            Tableau de bord
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-12">
        <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase">CoachDIA · Discovery</div>
        <h1 className="font-serif text-4xl text-[#0F0F10] mt-2">Vos coachs recommandés</h1>
        <p className="text-[#4B5563] mt-3">
          Sélection basée sur votre profil :
          <span className="ml-2 inline-flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: ENERGY_HEX[data.dominant_color] }} />
            <b>{ENERGY_LABEL[data.dominant_color]}</b> dominant · <b>{ENERGY_LABEL[data.secondary_color]}</b> secondaire
          </span>
        </p>

        {data.recommended_specialties?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {data.recommended_specialties.map(s => (
              <span key={s} className="text-[11px] tracking-widest text-[#92710A] border border-[#C4B27A] px-3 py-1 rounded-full bg-white">
                {s}
              </span>
            ))}
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(data.coaches || []).map(c => (
            <Card key={c.user_id} className="bg-white border border-[#E5DFD0] rounded-sm p-5" data-testid={`recommended-coach-${c.user_id}`}>
              <div className="flex items-center gap-3 mb-3">
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover border border-[#E5DFD0]" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#0F0F10] text-white flex items-center justify-center font-serif">
                    {c.display_name?.[0]?.toUpperCase() || 'C'}
                  </div>
                )}
                <div>
                  <div className="font-serif text-[#0F0F10]">{c.display_name || 'Coach'}</div>
                  {c.rating ? (
                    <div className="text-xs text-[#92710A] flex items-center gap-1 mt-0.5">
                      <Star className="w-3 h-3 fill-[#92710A]" /> {Number(c.rating).toFixed(1)}
                      <span className="text-[#4B5563] ml-1">· {c.total_sessions || 0} sessions</span>
                    </div>
                  ) : (
                    <div className="text-xs text-[#4B5563]">{c.total_sessions || 0} sessions</div>
                  )}
                </div>
              </div>
              {c.bio_short && <p className="text-sm text-[#4B5563] leading-relaxed line-clamp-3">{c.bio_short}</p>}
              {c.specialties?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {c.specialties.slice(0, 3).map(s => (
                    <span key={s} className="text-[10px] tracking-wider text-[#4B5563] border border-[#E5DFD0] px-2 py-0.5 rounded-sm">{s}</span>
                  ))}
                </div>
              )}
              <Button
                data-testid={`recommended-coach-cta-${c.user_id}`}
                onClick={() => navigate(`/marketplace/coach/${c.user_id}`)}
                className="mt-4 w-full bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full"
              >
                Voir le profil
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Card>
          ))}
        </div>

        {(!data.coaches || data.coaches.length === 0) && (
          <div className="mt-12 text-center text-[#4B5563]">
            Aucun coach disponible pour votre profil pour le moment. Explorez la marketplace.
          </div>
        )}

        <div className="mt-12 text-center">
          <Button onClick={() => navigate('/discovery')} variant="outline" className="rounded-full border-[#C4B27A] text-[#0F0F10]">
            Retour à mon rapport
          </Button>
        </div>
      </div>
    </div>
  );
}
